"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Download, Eye, Copy, Crown } from "lucide-react";
import { formatCurrency, formatDate, copyToClipboard } from "@/lib/utils";
import { Invoice, Customer, Vehicle, Appointment } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { PaginationMeta } from "@/types/appointment.api.types";
import { toast } from "sonner";

interface BillingTableQuery {
  page: number;
  limit: number;
}

interface InvoicesTableProps {
  invoices: Invoice[];
  payments: PaymentOutput[];
  customers: Customer[];
  vehicles: Vehicle[];
  appointments: Appointment[];
  getStatusBadge: (status: string) => React.ReactElement;
  onView: (invoice: Invoice) => void;
  onDownload: (invoice: Invoice) => void;
  onViewPayment?: (payment: PaymentOutput) => void;
  onUpdatePaymentStatus?: (
    payment: PaymentOutput,
    newStatus: string,
  ) => Promise<void>;
  onDownloadReceipt?: (payment: PaymentOutput) => void;
  query: BillingTableQuery;
  pagination: PaginationMeta;
  onQueryChange: (query: BillingTableQuery) => void;
}

export function InvoicesTable({
  invoices,
  payments,
  customers,
  vehicles,
  appointments,
  getStatusBadge,
  onView,
  onDownload,
  onViewPayment,
  onUpdatePaymentStatus,
  onDownloadReceipt,
  query,
  pagination,
  onQueryChange,
}: InvoicesTableProps) {
  const getPaymentStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: "bg-yellow-500 hover:bg-yellow-600",
      COMPLETED: "bg-green-500 hover:bg-green-600",
      FAILED: "bg-red-500 hover:bg-red-600",
    };

    return (
      <Badge className={colors[status] || "bg-gray-500 hover:bg-gray-600"}>
        {status}
      </Badge>
    );
  };

  const getModeBadge = (mode: string) => {
    return (
      <Badge variant="outline" className="capitalize">
        {mode}
      </Badge>
    );
  };

  const handleSharePayment = async (payment: PaymentOutput) => {
    try {
      const receiptUrl = `${window.location.origin}/receipt/${payment.id}`;
      await copyToClipboard(receiptUrl);
      toast.success("Receipt link copied to clipboard!");
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Failed to copy link to clipboard");
    }
  };

  const totalCount = invoices.length + payments.length;

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      onQueryChange({ ...query, page });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLimitChange = (limit: number) => {
    onQueryChange({ ...query, limit, page: 1 });
  };

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisiblePages = 5;

    if (pagination.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (pagination.page <= 3) {
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(pagination.totalPages);
      } else if (pagination.page >= pagination.totalPages - 2) {
        pages.push("ellipsis");
        for (let i = pagination.totalPages - 3; i <= pagination.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push("ellipsis");
        for (let i = pagination.page - 1; i <= pagination.page + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(pagination.totalPages);
      }
    }

    return pages;
  };

  const startIndex = pagination.total > 0
    ? (pagination.page - 1) * pagination.limit + 1
    : 0;
  const endIndex = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Invoices & Payments ({pagination.total || totalCount})</CardTitle>
          {pagination.total > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {startIndex} - {endIndex} of {pagination.total}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>ID/Invoice #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Ref ID</TableHead>
              <TableHead>Service Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead>Payment Method</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {totalCount === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  className="text-center text-muted-foreground"
                >
                  No invoices or payments found
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* Invoice rows */}
                {invoices.map((invoice) => {
                  const customer = customers.find(
                    (c) => c.id === invoice.customerId,
                  );
                  const vehicle = vehicles.find(
                    (v) => v.id === invoice.vehicleId,
                  );
                  const appointment = appointments.find(
                    (a) => a.id === invoice.appointmentId,
                  );

                  return (
                    <TableRow key={`invoice-${invoice.id}`}>
                      <TableCell>
                        <Badge variant="secondary">Invoice</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>{formatDate(invoice.date)}</TableCell>
                      <TableCell>{customer?.name || "N/A"}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs">
                          {appointment?.bookingRef ||
                            invoice.subscriptionId ||
                            "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {invoice.subscriptionId
                            ? "Subscription"
                            : appointment?.type === "USER_SUBSCRIPTION"
                              ? "Subscription"
                              : "One-Time"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(invoice.total)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(invoice.paymentStatus)}
                      </TableCell>
                      <TableCell className="capitalize">
                        {invoice.paymentMethod || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onView(invoice)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDownload(invoice)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Payment rows */}
                {payments.map((payment) => {
                  const paymentCustomer = payment.customer?.id
                    ? customers.find((c) => c.id === payment.customer?.id)
                    : null;

                  // Try to find associated appointment if refType is APPOINTMENT
                  const paymentAppointment =
                    payment.refType === "APPOINTMENT" && payment.refId
                      ? appointments.find((a) => a.id === payment.refId)
                      : null;

                  return (
                    <TableRow key={`payment-${payment.id}`}>
                      <TableCell>
                        <Badge variant="outline">Payment</Badge>
                      </TableCell>
                      <TableCell className="font-medium font-mono text-sm">
                        {payment.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell>
                        {payment.date
                          ? formatDate(new Date(payment.date))
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {paymentCustomer?.name ||
                          payment.customer?.name ||
                          "N/A"}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs">
                          {paymentAppointment?.bookingRef ||
                            (payment.refType === "APPOINTMENT"
                              ? "-"
                              : payment.refId || "-")}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{payment.refType}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {(payment.discount > 0 || payment.platinumDiscount > 0) ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1">
                              <span>{formatCurrency(payment.dueAmount)}</span>
                              {payment.platinumDiscount > 0 && (
                                <Crown className="h-3 w-3 text-amber-500" title="Platinum Member Discount Applied" />
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground line-through">
                              {formatCurrency(payment.totalAmount)}
                            </span>
                          </div>
                        ) : (
                          formatCurrency(payment.totalAmount)
                        )}
                      </TableCell>
                      <TableCell>
                        {getPaymentStatusBadge(payment.status)}
                      </TableCell>
                      <TableCell>{getModeBadge(payment.mode)}</TableCell>
                      <TableCell className="text-right">
                        {onViewPayment ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onViewPayment(payment)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSharePayment(payment)}
                              title="Copy Receipt Link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            {onDownloadReceipt && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => onDownloadReceipt(payment)}
                                title="Download Receipt"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>

        {pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page:</span>
              <Select
                value={String(query.limit || 10)}
                onValueChange={(value) => handleLimitChange(Number(value))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (pagination.hasPrev) {
                        handlePageChange(pagination.page - 1);
                      }
                    }}
                    className={
                      !pagination.hasPrev
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {getPageNumbers().map((page, index) => {
                  if (page === "ellipsis") {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }

                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                        isActive={pagination.page === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (pagination.hasNext) {
                        handlePageChange(pagination.page + 1);
                      }
                    }}
                    className={
                      !pagination.hasNext
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
